import { NavLink, Outlet } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import { formatUsd } from "../lib/format";

export function AdminLayout() {
  return (
    <div className="wrap admin-grid">
      <aside className="admin-side card">
        <NavLink to="/admin" end>Overview</NavLink>
        <NavLink to="/admin/markets">Markets</NavLink>
        <NavLink to="/admin/users">Users</NavLink>
        <NavLink to="/admin/finance">Finance</NavLink>
        <NavLink to="/admin/risk">Risk</NavLink>
      </aside>
      <div><Outlet /></div>
    </div>
  );
}

export function AdminOverview() {
  const q = useQuery({ queryKey: ["admin-ov"], queryFn: () => api<Record<string, string | number>>("/admin/overview") });
  const p = useQuery({ queryKey: ["admin-prof"], queryFn: () => api<{ feesMicro: string; byCategory: { name: string; fees_micro: number; volume_micro: number }[] }>("/admin/profitability") });
  const d = q.data;
  return (
    <div>
      <h1>Command center</h1>
      <div className="grid-3">
        <Card k="Users" v={String(d?.users ?? "—")} />
        <Card k="Open markets" v={String(d?.openMarkets ?? "—")} />
        <Card k="Volume" v={formatUsd(d?.volumeMicro ?? 0, true)} />
        <Card k="Fees" v={formatUsd(d?.feesMicro ?? 0)} />
        <Card k="Deposits" v={formatUsd(d?.depositsMicro ?? 0, true)} />
        <Card k="Open interest" v={String(d?.openInterestShares ?? "—")} />
      </div>
      <h2>Revenue by category</h2>
      <table className="table">
        <thead><tr><th>Category</th><th>Fees</th><th>Volume</th></tr></thead>
        <tbody>
          {(p.data?.byCategory ?? []).map((r) => (
            <tr key={r.name}><td>{r.name}</td><td>{formatUsd(r.fees_micro)}</td><td>{formatUsd(r.volume_micro, true)}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Card({ k, v }: { k: string; v: string }) {
  return <div className="card"><div style={{ color: "var(--muted)" }}>{k}</div><strong>{v}</strong></div>;
}

export function AdminMarkets() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-m"], queryFn: () => api<{ markets: Array<Record<string, unknown>> }>("/admin/markets") });
  const [question, setQuestion] = useState("");
  const cats = useQuery({ queryKey: ["cats"], queryFn: () => api<{ categories: { id: string; name: string }[] }>("/markets/categories") });
  const create = useMutation({
    mutationFn: () => api("/admin/markets", {
      method: "POST",
      body: JSON.stringify({
        question,
        description: question,
        categoryId: cats.data?.categories[0]?.id,
        resolutionSource: "To be documented before publish",
        resolutionMethodology: "Primary source listed on the market.",
        resolutionRules: "Will be completed before publish. Market stays in DRAFT until rules are unambiguous.",
        closesAt: new Date(Date.now() + 864e5 * 90).toISOString(),
      }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-m"] }),
  });
  return (
    <div>
      <h1>Markets</h1>
      <form className="card" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <label className="field">New question (draft)<input value={question} onChange={(e) => setQuestion(e.target.value)} /></label>
        <button className="btn btn-primary">Create draft</button>
      </form>
      <table className="table">
        <thead><tr><th>Question</th><th>Status</th><th>YES</th><th>Fees</th><th></th></tr></thead>
        <tbody>
          {(q.data?.markets ?? []).map((m) => (
            <tr key={String(m.id)}>
              <td>{String(m.question)}</td>
              <td>{String(m.status)}</td>
              <td>{String(m.yes_price_cents)}%</td>
              <td>{formatUsd(m.fees_micro as number)}</td>
              <td>
                {m.status === "DRAFT" && <Action path={`/admin/markets/${m.id}/publish`} label="Publish" />}
                {m.status === "OPEN" && <Action path={`/admin/markets/${m.id}/pause`} label="Pause" />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Action({ path, label }: { path: string; label: string }) {
  const qc = useQueryClient();
  return (
    <button className="btn btn-ghost" onClick={async () => {
      await api(path, { method: "POST", body: "{}" });
      void qc.invalidateQueries({ queryKey: ["admin-m"] });
    }}>{label}</button>
  );
}

export function AdminUsers() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const r = useQuery({ queryKey: ["admin-u", q], queryFn: () => api<{ users: Array<Record<string, string>> }>(`/admin/users?q=${encodeURIComponent(q)}`) });
  const kyc = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api(`/admin/users/${id}/kyc`, { method: "POST", body: JSON.stringify({ status }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-u"] }),
  });
  const suspend = useMutation({
    mutationFn: (id: string) => api(`/admin/users/${id}/suspend`, { method: "POST", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-u"] }),
  });
  return (
    <div>
      <h1>Users</h1>
      <p style={{ color: "var(--muted)" }}>Withdrawals require KYC = VERIFIED. There is no automated vendor yet — verify people here after you check identity.</p>
      <input className="field" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} />
      <table className="table">
        <thead><tr><th>Email</th><th>Username</th><th>KYC</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {(r.data?.users ?? []).map((u) => {
            const id = u.id;
            if (!id) return null;
            return (
            <tr key={id}>
              <td>{u.email}</td>
              <td>{u.username}</td>
              <td>{u.kyc_status}</td>
              <td>{u.status}</td>
              <td>
                {u.kyc_status !== "VERIFIED" && (
                  <button className="btn btn-primary btn-sm" type="button" onClick={() => kyc.mutate({ id, status: "VERIFIED" })}>Verify KYC</button>
                )}{" "}
                {u.status !== "SUSPENDED" && u.role !== "SUPER_ADMIN" && (
                  <button className="btn btn-ghost btn-sm" type="button" onClick={() => suspend.mutate(id)}>Suspend</button>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function AdminFinance() {
  const qc = useQueryClient();
  const l = useQuery({ queryKey: ["admin-l"], queryFn: () => api<{ transactions: Array<Record<string, string>> }>("/admin/ledger") });
  const w = useQuery({ queryKey: ["admin-w"], queryFn: () => api<{ withdrawals: Array<Record<string, string>> }>("/admin/withdrawals") });
  const approve = useMutation({
    mutationFn: (id: string) => api(`/admin/withdrawals/${id}/approve`, { method: "POST", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-w"] }),
  });
  const reject = useMutation({
    mutationFn: (id: string) => api(`/admin/withdrawals/${id}/reject`, { method: "POST", body: JSON.stringify({ reason: "Rejected in admin finance" }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-w"] }),
  });
  return (
    <div>
      <h1>Finance</h1>
      <h3>Withdrawals</h3>
      {(approve.error || reject.error) && (
        <p style={{ color: "var(--danger)" }}>{((approve.error || reject.error) as Error).message}</p>
      )}
      <table className="table">
        <thead><tr><th>User</th><th>Amount</th><th>Destination</th><th>Network</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {(w.data?.withdrawals ?? []).map((x) => (
            <tr key={x.id}>
              <td>{x.user_id}</td>
              <td>{formatUsd(x.amount_micro ?? 0)}</td>
              <td style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{x.destination}</td>
              <td>{x.network_id || x.provider}</td>
              <td>{x.status}</td>
              <td>
                {x.id && (x.status === "RISK_REVIEW" || x.status === "APPROVED") && (
                  <>
                    <button className="btn btn-primary btn-sm" type="button" onClick={() => approve.mutate(x.id!)}>Approve</button>{" "}
                    <button className="btn btn-ghost btn-sm" type="button" onClick={() => reject.mutate(x.id!)}>Reject</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3>Ledger</h3>
      <table className="table">
        <thead><tr><th>Type</th><th>User</th><th>Time</th></tr></thead>
        <tbody>
          {(l.data?.transactions ?? []).map((t) => (
            <tr key={t.id}><td>{t.type}</td><td>{t.user_id}</td><td>{t.created_at}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminRisk() {
  const q = useQuery({ queryKey: ["admin-r"], queryFn: () => api<{ events: Array<Record<string, string>> }>("/admin/risk") });
  const a = useQuery({ queryKey: ["admin-a"], queryFn: () => api<{ logs: Array<Record<string, string>> }>("/admin/audit") });
  return (
    <div>
      <h1>Risk & audit</h1>
      <p>Alerts are for review — they do not automatically accuse a user.</p>
      <table className="table">
        <thead><tr><th>Type</th><th>Severity</th><th>Details</th></tr></thead>
        <tbody>
          {(q.data?.events ?? []).map((e) => (
            <tr key={e.id}><td>{e.type}</td><td>{e.severity}</td><td>{e.details}</td></tr>
          ))}
        </tbody>
      </table>
      <h3>Audit log</h3>
      <table className="table">
        <thead><tr><th>Action</th><th>Admin</th><th>Time</th></tr></thead>
        <tbody>
          {(a.data?.logs ?? []).map((e) => (
            <tr key={e.id}><td>{e.action}</td><td>{e.admin_id}</td><td>{e.created_at}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
